import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  Smartphone, 
  Globe, 
  Apple,
  Play,
  ExternalLink,
  Star
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sampleApps = [
  {
    app_id: 'sample_app_1',
    name: 'Nspire Meditation',
    description: 'A guided meditation app with daily sessions designed to reduce stress and increase mental clarity. Features 100+ guided meditations, sleep stories, and breathing exercises.',
    platform: 'both',
    icon_url: 'https://images.unsplash.com/photo-1643184012410-0d0c9070e575?w=200&q=80',
    app_store_url: '#',
    play_store_url: '#',
    web_url: '#',
    rating: 4.8,
    downloads: '100K+'
  },
  {
    app_id: 'sample_app_2',
    name: 'Freedom Finance',
    description: 'Track your spending, set savings goals, and build your path to financial independence. Smart budgeting tools and investment tracking all in one place.',
    platform: 'mobile',
    icon_url: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=200&q=80',
    app_store_url: '#',
    play_store_url: '#',
    rating: 4.6,
    downloads: '50K+'
  },
  {
    app_id: 'sample_app_3',
    name: 'Anywhere Fitness',
    description: 'Your personal trainer in your pocket. Workout routines that require no equipment and can be done anywhere in the world.',
    platform: 'web',
    icon_url: 'https://images.unsplash.com/photo-1562088287-bde35a1ea917?w=200&q=80',
    web_url: '#',
    rating: 4.9,
    downloads: '200K+'
  }
];

export default function AppsPage() {
  const { currentTheme } = useTheme();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchApps();
  }, [filter]);

  const fetchApps = async () => {
    try {
      const params = filter !== 'all' ? `?platform=${filter}` : '';
      const response = await axios.get(`${API}/apps${params}`);
      const dbApps = response.data || [];
      setApps(dbApps.length > 0 ? dbApps : sampleApps);
    } catch (error) {
      console.error('Error fetching apps:', error);
      setApps(sampleApps);
    } finally {
      setLoading(false);
    }
  };

  const filteredApps = filter === 'all' ? apps : apps.filter(app => 
    app.platform === filter || app.platform === 'both'
  );

  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'mobile': return Smartphone;
      case 'web': return Globe;
      case 'both': return Smartphone;
      default: return Smartphone;
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-medium mb-4" style={{ color: currentTheme.foreground }}>
              Our Apps
            </h1>
            <p className="font-body text-lg opacity-70">
              Tools we've built to support your journey to financial, mental, and physical freedom.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex justify-center gap-2 mb-12">
            {[
              { value: 'all', label: 'All Apps', icon: null },
              { value: 'mobile', label: 'Mobile', icon: Smartphone },
              { value: 'web', label: 'Web', icon: Globe },
            ].map((tab) => (
              <Button
                key={tab.value}
                variant={filter === tab.value ? 'default' : 'outline'}
                onClick={() => setFilter(tab.value)}
                className="gap-2"
                style={filter === tab.value ? { backgroundColor: currentTheme.primary } : {}}
                data-testid={`apps-filter-${tab.value}`}
              >
                {tab.icon && <tab.icon className="w-4 h-4" />}
                {tab.label}
              </Button>
            ))}
          </div>

          {/* Apps Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-96 rounded-2xl" />
              ))}
            </div>
          ) : filteredApps.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredApps.map((app) => {
                const PlatformIcon = getPlatformIcon(app.platform);
                return (
                  <div
                    key={app.app_id}
                    className="rounded-2xl overflow-hidden card-hover"
                    style={{ backgroundColor: 'white', boxShadow: '0 4px 30px rgba(0,0,0,0.05)' }}
                    data-testid={`app-card-${app.app_id}`}
                  >
                    {/* App Header */}
                    <div 
                      className="p-6 flex items-center gap-4"
                      style={{ background: `linear-gradient(135deg, ${currentTheme.primary}10, ${currentTheme.accent}10)` }}
                    >
                      <img
                        src={app.icon_url}
                        alt={app.name}
                        className="w-20 h-20 rounded-2xl object-cover shadow-lg"
                      />
                      <div>
                        <h3 className="font-display text-xl font-medium" style={{ color: currentTheme.foreground }}>
                          {app.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            <Star className="w-4 h-4 fill-current" style={{ color: currentTheme.secondary }} />
                            <span className="font-body text-sm font-medium">{app.rating || '4.5'}</span>
                          </div>
                          <span className="text-sm opacity-60">•</span>
                          <span className="font-body text-sm opacity-60">{app.downloads || '10K+'} downloads</span>
                        </div>
                      </div>
                    </div>

                    {/* App Body */}
                    <div className="p-6">
                      <p className="font-body leading-relaxed opacity-70 mb-6">
                        {app.description}
                      </p>

                      {/* Platform Badge */}
                      <div className="flex items-center gap-2 mb-6">
                        <span 
                          className="px-3 py-1 rounded-full text-sm font-medium capitalize flex items-center gap-1"
                          style={{ backgroundColor: `${currentTheme.primary}10`, color: currentTheme.primary }}
                        >
                          <PlatformIcon className="w-4 h-4" />
                          {app.platform === 'both' ? 'Mobile & Web' : app.platform}
                        </span>
                      </div>

                      {/* Download Links */}
                      <div className="flex flex-wrap gap-3">
                        {(app.platform === 'mobile' || app.platform === 'both') && (
                          <>
                            {app.app_store_url && (
                              <a href={app.app_store_url} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="gap-2">
                                  <Apple className="w-4 h-4" />
                                  App Store
                                </Button>
                              </a>
                            )}
                            {app.play_store_url && (
                              <a href={app.play_store_url} target="_blank" rel="noopener noreferrer">
                                <Button variant="outline" size="sm" className="gap-2">
                                  <Play className="w-4 h-4" />
                                  Play Store
                                </Button>
                              </a>
                            )}
                          </>
                        )}
                        {(app.platform === 'web' || app.platform === 'both') && app.web_url && (
                          <a href={app.web_url} target="_blank" rel="noopener noreferrer">
                            <Button 
                              size="sm" 
                              className="gap-2"
                              style={{ backgroundColor: currentTheme.primary }}
                            >
                              <ExternalLink className="w-4 h-4" />
                              Open Web App
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <Smartphone className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
                No apps found
              </h3>
              <p className="font-body opacity-60">
                Check back soon for new apps!
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
